package com.milta.feature.auth.data.remote

import com.milta.core.network.Endpoints
import com.milta.core.network.baseUrl
import com.milta.feature.auth.data.remote.dto.ApiError
import com.milta.feature.auth.data.remote.dto.AppleExchangeResponse
import com.milta.feature.auth.data.remote.dto.AuthResponse
import com.milta.feature.auth.data.remote.dto.AuthenticateWithOAuthRequest
import com.milta.feature.auth.data.remote.dto.CheckAvailabilityResponse
import com.milta.feature.auth.data.remote.dto.GoogleExchangeResponse
import com.milta.feature.auth.data.remote.dto.OAuthAuthenticateResponse
import com.milta.feature.auth.data.remote.dto.UpdatePendingPhoneRequest
import com.milta.feature.auth.data.remote.dto.LogoutRequest
import com.milta.feature.auth.data.remote.dto.OtpSendRequest
import com.milta.feature.auth.data.remote.dto.OtpVerifyRequest
import com.milta.feature.auth.data.remote.dto.RefreshRequest
import com.milta.feature.auth.data.remote.dto.RegisterWithOAuthRequest
import com.milta.feature.auth.data.remote.dto.TokenResponse
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.parameter
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.contentType
import io.ktor.http.isSuccess

class AuthApi(private val client: HttpClient) {

    suspend fun registerWithOAuth(request: RegisterWithOAuthRequest): AuthResponse {
        val response = client.post("$baseUrl/${Endpoints.authRegisterOAuth}") {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        if (!response.status.isSuccess()) {
            val apiError = runCatching { response.body<ApiError>() }.getOrNull()
            val message = apiError?.code ?: "error_${response.status.value}"
            throw ApiException(response.status.value, message)
        }
        return response.body()
    }

    suspend fun authenticateWithOAuth(request: AuthenticateWithOAuthRequest): OAuthAuthenticateResponse {
        val response = client.post("$baseUrl/${Endpoints.authOAuthAuthenticate}") {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        if (!response.status.isSuccess()) {
            val apiError = runCatching { response.body<ApiError>() }.getOrNull()
            val message = apiError?.code ?: "error_${response.status.value}"
            throw ApiException(response.status.value, message)
        }
        return response.body()
    }

    suspend fun updatePendingPhone(request: UpdatePendingPhoneRequest): OAuthAuthenticateResponse {
        val response = client.post("$baseUrl/${Endpoints.authOAuthUpdatePhone}") {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        if (!response.status.isSuccess()) {
            val apiError = runCatching { response.body<ApiError>() }.getOrNull()
            val message = apiError?.code ?: "error_${response.status.value}"
            throw ApiException(response.status.value, message)
        }
        return response.body()
    }

    suspend fun requestOtp(request: OtpSendRequest) {
        val response = client.post("$baseUrl/${Endpoints.authOtpRequest}") {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        if (!response.status.isSuccess()) {
            val apiError = runCatching { response.body<ApiError>() }.getOrNull()
            val message = apiError?.code ?: "error_${response.status.value}"
            throw ApiException(response.status.value, message)
        }
    }

    suspend fun verifyOtp(request: OtpVerifyRequest): TokenResponse {
        val response = client.post("$baseUrl/${Endpoints.authOtpVerify}") {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        if (!response.status.isSuccess()) {
            val apiError = runCatching { response.body<ApiError>() }.getOrNull()
            val message = apiError?.code ?: "error_${response.status.value}"
            throw ApiException(response.status.value, message)
        }
        return response.body()
    }

    suspend fun refresh(request: RefreshRequest): TokenResponse {
        val response = client.post("$baseUrl/${Endpoints.authRefresh}") {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        if (!response.status.isSuccess()) {
            val apiError = runCatching { response.body<ApiError>() }.getOrNull()
            val message = apiError?.code ?: "error_${response.status.value}"
            throw ApiException(response.status.value, message)
        }
        return response.body()
    }

    suspend fun logout(request: LogoutRequest) {
        val response = client.post("$baseUrl/${Endpoints.authLogout}") {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        if (!response.status.isSuccess()) {
            val apiError = runCatching { response.body<ApiError>() }.getOrNull()
            val message = apiError?.code ?: "error_${response.status.value}"
            throw ApiException(response.status.value, message)
        }
    }

    suspend fun checkAvailability(email: String?, phone: String?): CheckAvailabilityResponse {
        val response = client.get("$baseUrl/${Endpoints.authCheck}") {
            email?.let { parameter("email", it) }
            phone?.let { parameter("phone", it) }
        }
        if (!response.status.isSuccess()) {
            val apiError = runCatching { response.body<ApiError>() }.getOrNull()
            val message = apiError?.code ?: "error_${response.status.value}"
            throw ApiException(response.status.value, message)
        }
        return response.body()
    }

    /**
     * Exchanges the opaque `state` (received via the app's deep link after the Apple Custom Tabs
     * redirect) for the ID token captured by the backend's `/auth/oauth/apple/callback`. Single-use.
     */
    suspend fun exchangeAppleCallback(state: String): AppleExchangeResponse {
        val response = client.get("$baseUrl/${Endpoints.authOAuthAppleExchange}") {
            parameter("state", state)
        }
        if (!response.status.isSuccess()) {
            val apiError = runCatching { response.body<ApiError>() }.getOrNull()
            val message = apiError?.code ?: "error_${response.status.value}"
            throw ApiException(response.status.value, message)
        }
        return response.body()
    }

    /**
     * Exchanges the opaque `state` (received via the app's deep link after the Google Custom Tabs
     * redirect) for the ID token captured by the backend's `/auth/oauth/google/callback`. Single-use.
     * Only used by the browser fallback — the native Credential Manager path never calls this.
     */
    suspend fun exchangeGoogleCallback(state: String): GoogleExchangeResponse {
        val response = client.get("$baseUrl/${Endpoints.authOAuthGoogleExchange}") {
            parameter("state", state)
        }
        if (!response.status.isSuccess()) {
            val apiError = runCatching { response.body<ApiError>() }.getOrNull()
            val message = apiError?.code ?: "error_${response.status.value}"
            throw ApiException(response.status.value, message)
        }
        return response.body()
    }

    suspend fun logoutAll() {
        val response = client.post("$baseUrl/${Endpoints.authLogoutAll}") {
            contentType(ContentType.Application.Json)
        }
        if (!response.status.isSuccess()) {
            val apiError = runCatching { response.body<ApiError>() }.getOrNull()
            val message = apiError?.code ?: "error_${response.status.value}"
            throw ApiException(response.status.value, message)
        }
    }
}
